import api from './api';
import type { AccountantReportResponse } from '@crm/shared';

type AccountantReportParams = { productId: number; start: string; end: string };

async function downloadBlob(
  url: string,
  params: AccountantReportParams & { format: 'xlsx' | 'pdf' }
) {
  const response = await api.get(url, { params, responseType: 'blob' });
  const disposition = response.headers['content-disposition'] as string | undefined;
  const filename =
    disposition?.match(/filename="?([^";]+)"?/)?.[1] ?? `relatorio-contador.${params.format}`;
  const href = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(href);
}

export const financialOpsService = {
  // Companies
  listCompanies: (params?: Record<string, any>) =>
    api.get('/financial/companies', { params }).then((r) => r.data),

  getCompany: (id: string) => api.get(`/financial/companies/${id}`).then((r) => r.data),

  createCompany: (data: Record<string, any>) =>
    api.post('/financial/companies', data).then((r) => r.data),

  updateCompany: (id: string, data: Record<string, any>) =>
    api.put(`/financial/companies/${id}`, data).then((r) => r.data),

  deactivateCompany: (id: string) => api.delete(`/financial/companies/${id}`).then((r) => r.data),

  // Bank Accounts
  listBankAccounts: (companyId: string, params?: Record<string, any>) =>
    api.get('/financial/bank-accounts', { params: { companyId, ...params } }).then((r) => r.data),

  createBankAccount: (data: Record<string, any>) =>
    api.post('/financial/bank-accounts', data).then((r) => r.data),

  updateBankAccount: (id: string, data: Record<string, any>) =>
    api.put(`/financial/bank-accounts/${id}`, data).then((r) => r.data),

  deactivateBankAccount: (id: string) =>
    api.delete(`/financial/bank-accounts/${id}`).then((r) => r.data),

  // Categories
  listCategories: (params?: Record<string, any>) =>
    api.get('/financial/categories', { params }).then((r) => r.data),

  createCategory: (data: Record<string, any>) =>
    api.post('/financial/categories', data).then((r) => r.data),

  updateCategory: (id: string, data: Record<string, any>) =>
    api.put(`/financial/categories/${id}`, data).then((r) => r.data),

  // Transactions
  listTransactions: (params?: Record<string, any>) =>
    api.get('/financial/transactions', { params }).then((r) => r.data),

  createTransaction: (data: Record<string, any>) =>
    api.post('/financial/transactions', data).then((r) => r.data),

  updateTransaction: (id: string, data: Record<string, any>) =>
    api.put(`/financial/transactions/${id}`, data).then((r) => r.data),

  deleteTransaction: (id: string) =>
    api.delete(`/financial/transactions/${id}`).then((r) => r.data),

  markTransactionPaid: (id: string, paidDate?: string) =>
    api.post(`/financial/transactions/${id}/mark-paid`, { paidDate }).then((r) => r.data),

  getCashFlowSummary: (companyId: string, startDate: string, endDate: string) =>
    api
      .get('/financial/transactions/cash-flow', { params: { companyId, startDate, endDate } })
      .then((r) => r.data),

  listAttachments: (transactionId: string) =>
    api.get(`/financial/transactions/${transactionId}/attachments`).then((r) => r.data),

  uploadAttachment: (transactionId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post(`/financial/transactions/${transactionId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  deleteAttachment: (id: string) => api.delete(`/financial/attachments/${id}`).then((r) => r.data),

  attachmentDownloadUrl: (id: string) => `/api/financial/attachments/${id}/download`,

  // Taxes
  listTaxes: (params?: Record<string, any>) =>
    api.get('/financial/taxes', { params }).then((r) => r.data),

  createTax: (data: Record<string, any>) => api.post('/financial/taxes', data).then((r) => r.data),

  updateTax: (id: string, data: Record<string, any>) =>
    api.put(`/financial/taxes/${id}`, data).then((r) => r.data),

  deleteTax: (id: string) => api.delete(`/financial/taxes/${id}`).then((r) => r.data),

  markTaxPaid: (id: string, paidDate?: string) =>
    api.post(`/financial/taxes/${id}/mark-paid`, { paidDate }).then((r) => r.data),

  // Payouts
  listPayouts: (params?: Record<string, any>) =>
    api.get('/financial/payouts', { params }).then((r) => r.data),

  syncPayouts: () => api.post('/financial/payouts/sync').then((r) => r.data),

  confirmPayout: (
    id: string,
    payload: { companyId: string; bankAccountId: string; notes?: string }
  ) => api.post(`/financial/payouts/${id}/confirm`, payload).then((r) => r.data),

  disputePayout: (id: string, notes?: string) =>
    api.post(`/financial/payouts/${id}/dispute`, { notes }).then((r) => r.data),

  revertPayout: (id: string) => api.post(`/financial/payouts/${id}/revert`).then((r) => r.data),

  linkPayoutBankAccount: (id: string, bankAccountId: string) =>
    api.put(`/financial/payouts/${id}/bank-account`, { bankAccountId }).then((r) => r.data),

  // Monthly Closings
  listClosings: (companyId: string, year?: number) =>
    api
      .get('/financial/closings', { params: { companyId, ...(year ? { year } : {}) } })
      .then((r) => r.data),

  createOrRecalculateClosing: (companyId: string, month: number, year: number) =>
    api.post('/financial/closings', { companyId, month, year }).then((r) => r.data),

  getClosing: (id: string) => api.get(`/financial/closings/${id}`).then((r) => r.data),

  finalizeClosing: (id: string) =>
    api.post(`/financial/closings/${id}/finalize`).then((r) => r.data),

  reopenClosing: (id: string) => api.post(`/financial/closings/${id}/reopen`).then((r) => r.data),

  exportClosing: (id: string) => api.get(`/financial/closings/${id}/export`).then((r) => r.data),

  getDre: (id: string) => api.get(`/financial/closings/${id}/dre`).then((r) => r.data),

  downloadDre: async (id: string, format: 'xlsx' | 'pdf') => {
    const response = await api.get(`/financial/closings/${id}/dre/export`, {
      params: { format },
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const match = disposition?.match(/filename="?([^";]+)"?/);
    const filename = match?.[1] ?? `dre.${format}`;
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  // DocuGen accountant report — exported data is already sanitized by the API.
  getAccountantReport: (params: AccountantReportParams) =>
    api
      .get<AccountantReportResponse>('/financial/accountant-report', { params })
      .then((r) => r.data),

  getClosingAccountantReport: (id: string) =>
    api
      .get<AccountantReportResponse>(`/financial/closings/${id}/accountant-report`)
      .then((r) => r.data),

  syncClosingAccountantReport: (id: string) =>
    api
      .post<AccountantReportResponse>(`/financial/closings/${id}/accountant-report/sync`)
      .then((r) => r.data),

  downloadAccountantReport: (params: AccountantReportParams & { format: 'xlsx' | 'pdf' }) =>
    downloadBlob('/financial/accountant-report/export', params),

  // Simulador da Reforma Tributária
  getTaxReformDefaults: (companyId: string) =>
    api.get('/financial/tax-reform/defaults', { params: { companyId } }).then((r) => r.data),

  simulateTaxReform: (data: Record<string, any>) =>
    api.post('/financial/tax-reform/simulate', data).then((r) => r.data),
};
